import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import { X } from "phosphor-react";
import React, { CSSProperties, Fragment, FunctionComponent, useState } from "react";
import { BrandContext } from "../../../contexts/BrandContext";
import { BrandButtonColorStyle } from "../../../utils/enums";
import TopRoundedSpinner from "../Spinner/TopRoundedSpinner";
import BrandButton from "../custom-components/BrandButton";

export interface ModalProps {
  open: boolean;
  modalStyle?: CSSProperties;
}

interface Props extends ModalProps {
  theme?: "primary" | "secondary";
  onClose: VoidFunction;
  className?: string;
  modalClassName?: string;
  parentClassNames?: string;
  overlayClassNames?: string;
  loading?: boolean;
  showCross?: boolean;
  hideScrollBar?: boolean;
  crossClassnames?: string;
  closeOnOutSideClick?: boolean;
  noModalBG?: boolean;
  fullscreen?: boolean;
  askConfirmation?: boolean;
  confirmationMessage?: string;
}

const CustomModal: FunctionComponent<Props> = ({
  open,
  onClose,
  children,
  className,
  modalClassName,
  theme = "primary",
  parentClassNames,
  overlayClassNames,
  loading,
  showCross,
  crossClassnames,
  hideScrollBar,
  closeOnOutSideClick = true,
  noModalBG,
  fullscreen = false,
  askConfirmation = false,
  confirmationMessage = "Are you sure you want to close?",
}) => {
  const brandValue = React.useContext(BrandContext);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleClose = () => {
    if (askConfirmation) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const cancelClose = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className={`fixed z-[999] inset-0 ${fullscreen ? "w-screen h-screen" : ""} overflow-y-auto ${className}`}
          onClose={() => {
            if (closeOnOutSideClick) {
              handleClose();
            }
          }}
        >
          <div
            className={classNames(
              `flex justify-center min-h-screen min-w-screen ${fullscreen ? "items-center w-screen h-screen px-0 pt-0 pb-0" : "px-4 pt-4 pb-20 items-end"} text-center sm:block sm:p-2 ${parentClassNames}`,
              {
                " sm:flex items-center": theme === "secondary",
              }
            )}
          >
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className={`fixed inset-0 transition-opacity bg-black/80 ${overlayClassNames}`}
            />

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              className={`relative ${fullscreen ? "flex flex-col w-full h-full" : "inline-block"} drop-shadow-socialConnect align-middle ${theme === "secondary"
                ? `rounded-tl-3xl rounded-br-3xl p-0.5 ${brandValue.brandButtonColorStyle ===
                  BrandButtonColorStyle.GRADIENT
                  ? "gradient-button"
                  : "gradient-itself"
                }`
                : "rounded-xl"
                } ${modalClassName}`}
            >
              {showCross ? (
                <button
                  onClick={handleClose}
                  className={classNames(
                    "absolute top-5 right-5 z-40",
                    crossClassnames
                  )}
                >
                  <X
                    className={classNames("w-6 h-6 text-primaryText")}
                    weight="bold"
                  />
                </button>
              ) : null}

              {loading ? (
                <div
                  className={classNames(
                    "absolute inset-2 flex justify-center items-center backdrop-blur-md z-50 bg-opacity-50 !overflow-hidden ",
                    theme === "primary"
                      ? "rounded-xl"
                      : "rounded-tl-[23px] rounded-br-[23px]"
                  )}
                >
                  <TopRoundedSpinner />
                </div>
              ) : null}

              {!noModalBG ? (
                <div
                  id={"additional-div"}
                  className={classNames(
                    `overflow-hidden text-primaryText ${fullscreen ? "flex-1 flex flex-col w-full h-full px-0" : "px-4 md:px-8"}`,
                    theme === "primary"
                      ? "bg-white"
                      : "bg-white",
                    theme === "primary"
                      ? "rounded-xl"
                      : "rounded-tl-[23px] rounded-br-[23px]"
                  )}
                >
                  <div
                    className={classNames(
                      `relative text-left overflow-auto hideScrollBar ${fullscreen ? "w-full h-full" : "max-h-[70vh] sm:max-h-[85vh]"}`
                    )}
                  >
                    {children}
                  </div>
                </div>
              ) : (
                <div className={modalClassName}>{children}</div>
              )}
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <Transition.Root show={showConfirmation} as={Fragment}>
          <Dialog
            as="div"
            className="fixed z-[9999] inset-0 overflow-y-auto"
            onClose={cancelClose}
          >
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="fixed inset-0 transition-opacity bg-black/80"
              />

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>

              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                className="inline-block align-middle bg-primaryModal rounded-xl px-6 py-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full"
              >
                <div className="text-center">
                  <p className="text-xl font-medium text-primaryText mb-4">
                    Are you sure you want to close?
                  </p>
                  <p className="text-sm text-gray-500 text-primaryText mb-6">
                    {confirmationMessage}
                  </p>
                  <div className="flex justify-center space-x-4">
                    <BrandButton
                      onClick={cancelClose}
                      theme="secondary"
                    >
                      Cancel
                    </BrandButton>
                    <BrandButton
                      onClick={confirmClose}
                    >
                      Yes, Close
                    </BrandButton>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}
    </>
  );
};

export default CustomModal;
